import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Upload } from 'lucide-react';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';

const CORES: CorSelo[] = ['neutro', 'acento', 'roxo', 'azul', 'ciano', 'laranja', 'dourado', 'verde', 'perigo'];

function Lista({ recurso }: { recurso: 'tags' | 'setores' }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState<CorSelo>('neutro');
  const [importar, setImportar] = useState(false);
  const tituloImportar = t(recurso === 'tags' ? 'classificacao.importarTags' : 'classificacao.importarSetores');
  const q = useQuery({ queryKey: ['cadastros', recurso], queryFn: () => getTodos<{ id: string; nome: string; cor?: CorSelo; parceiros: number }>(`/cadastros/${recurso}`) });
  const criar = useMutation({
    mutationFn: () => post(`/cadastros/${recurso}`, recurso === 'tags' ? { nome, cor } : { nome }),
    onSuccess: () => {
      setNome('');
      return qc.invalidateQueries({ queryKey: ['cadastros', recurso] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({ mutationFn: (id: string) => del(`/cadastros/${recurso}/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['cadastros', recurso] }), onError: (e) => toast.erro(mensagemDeErro(e)) });
  return (
    <Cartao titulo={t(recurso === 'tags' ? 'campos.tags' : 'campos.setor')}>
      {s.pode('cadastros.editar') && (
        <form
          className="mb-4 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim()) criar.mutate();
          }}
        >
          <Entrada aria-label={t('campos.nome')} placeholder={t(recurso === 'tags' ? 'acoes.novaTag' : 'acoes.novoSetor')} value={nome} onChange={(e) => setNome(e.target.value)} className="min-w-0 flex-1 basis-40" maxLength={60} />
          {recurso === 'tags' && (
            <div className="w-36">
              <Selecao aria-label={t('campos.cor')} opcoes={CORES.map((c) => ({ valor: c, rotulo: t(`classificacao.cores.${c}`) }))} value={cor} onChange={(e) => setCor(e.target.value as CorSelo)} />
            </div>
          )}
          <Botao type="submit" variante="secundario" icone={<Plus className="size-4" />} carregando={criar.isPending}>
            {t('acoes.adicionar')}
          </Botao>
        </form>
      )}
      {/* Fase 2 (W18, K3): importador de tags e de setores (framework do núcleo, atualiza pelo nome). */}
      {s.pode('cadastros.importar') && s.pode('cadastros.editar') && (
        <div className="mb-4 flex flex-wrap gap-2" data-importar-classificacao={recurso}>
          <BotaoModeloCsv caminho={`/cadastros/${recurso}`} />
          <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
            {tituloImportar}
          </Botao>
        </div>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <ul className="divide-y divide-borda">
            {d.itens.map((i) => (
              // R-B5 (r8 robustez): nome longo sem espaço quebra dentro da linha (min-w-0 e quebra de palavra) e a coluna da
              // direita (contador e excluir) nunca encolhe; antes a linha passava de 434px a 390.
              <li key={i.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
                {recurso === 'tags' ? (
                  <Selo cor={i.cor ?? 'neutro'} quebra>
                    <span className="min-w-0 wrap-anywhere">{i.nome}</span>
                  </Selo>
                ) : (
                  <span className="min-w-0 wrap-anywhere">{i.nome}</span>
                )}
                <span className="flex shrink-0 items-center gap-3">
                  <span className="numero text-xs text-tinta-3">
                    {t('classificacao.qtdParceiros', { count: i.parceiros })}
                  </span>
                  {s.pode('cadastros.excluir') && (
                    <button type="button" aria-label={tn('comum.excluir')} onClick={() => remover.mutate(i.id)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Estado>
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={`/cadastros/${recurso}`} titulo={tituloImportar} aoConcluir={() => void qc.invalidateQueries({ queryKey: ['cadastros', recurso] })} />
    </Cartao>
  );
}

export function Classificacao() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Lista recurso="tags" />
      <Lista recurso="setores" />
    </div>
  );
}
